import moment from "moment";
import {useAccessToken} from "~/auth/hooks";
import {
    GetFinanceDocument,
    GetFinanceReconciliationDocument,
    GetFinanceReconciliationPathBuilder,
    GetFinanceReconciliationResult,
    GetFinanceReconciliationVars,
    GetFinanceResult,
    GetFinanceVars,
    GetTariffAgreementsDocument,
    GetTariffAgreementsResult,
    GetTariffAgreementsVars,
} from "~/apps/packages/finance/queries";
import {useQuery} from "~/apollo/patches";
import {useCallback, useEffect, useMemo} from "react";
import {useErrorHandler} from "~/components/ErrorHandling";
import {API_URL} from "~/config";
import {useLazyQuery} from "@apollo/client";
import {tuple} from "~/types";


export const useFileReconciliationLink = (fromDate: Date, toDate: Date) => {
    const session = useAccessToken();
    const accessToken = encodeURIComponent(session?.accessToken);

    if (!fromDate || !toDate) {
        return "";
    }

    const dateFrom = encodeURIComponent(moment(fromDate).format("YYYY-MM-DDT00:00:00"));
    const dateTo = encodeURIComponent(moment(toDate).format("YYYY-MM-DDT23:59:59"));
    return `${API_URL}remote/erp/VMTP_API/finances/reconciliation/?pdf=&date_from=${dateFrom}&date_to=${dateTo}&access_token=${accessToken}`;
}


export const useGetReconciliation = () => {
    const handleError = useErrorHandler();
    const [execute, {error, loading, data, variables}] = useLazyQuery<GetFinanceReconciliationResult, GetFinanceReconciliationVars>(GetFinanceReconciliationDocument, {
        notifyOnNetworkStatusChange: true
    });

    const get = useCallback(async ({dateFrom, dateTo, email}: {dateFrom: Date, dateTo: Date, email?: string}) => {
        await execute({
            variables: {
                pathBuilder: GetFinanceReconciliationPathBuilder,
                dateFrom: dateFrom,
                dateTo: dateTo,
                email: email,
            }
        }).catch(console.error);
    }, [execute]);


    useEffect(() => {
        if (error) {
            handleError(error);
        }
    }, [error])


    return tuple(get, {
        loading: loading,
        data: data?.financeReconciliation?.financeReconciliation,
        variables: variables ? {
            dateFrom: variables.dateFrom,
            dateTo: variables.dateTo,
        } : null
    })
}


export const useTariffAgreements = () => {
    const handleError = useErrorHandler();
    const session = useAccessToken();
    const accessToken = encodeURIComponent(session?.accessToken);
    const {data, loading, error, refetch} = useQuery<GetTariffAgreementsResult, GetTariffAgreementsVars>(GetTariffAgreementsDocument);

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutId = setTimeout(() => refetch(), 5000);
            return () => clearTimeout(timeoutId);
        }
    }, [error]);

    return {
        data: data?.tariffAgreements?.tariffAgreements?.map(item => ({
            ...item,
            fileLink: `${API_URL}remote/erp/api/v2/tariff-agreements/${item.id}?pdf=&access_token=${accessToken}`
        })),
        loading: loading || error != null,
    }
}


export const useFinance = () => {
    const handleError = useErrorHandler();
    const {data, loading, error, refetch} = useQuery<GetFinanceResult, GetFinanceVars>(GetFinanceDocument);

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutId = setTimeout(() => refetch(), 5000);
            return () => clearTimeout(timeoutId);
        }
    }, [error])

    return {data: data?.contractsBalance, loading: loading || error != null};
}
